/**
 * Passkeys in the browser.
 *
 * `navigator.credentials` is the platform API, so this needs no dependency. Everything
 * it does is translation: the server speaks base64url because JSON does, and WebAuthn
 * speaks `ArrayBuffer`, and getting that conversion wrong is the entire failure mode.
 *
 * The server verifies the ceremony (`apps/api/src/lib/webauthn.ts`); nothing here is
 * trusted, which is why this file can be as simple as it is.
 */

function toBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  // Backed by a plain `ArrayBuffer`, which is what `BufferSource` requires: a
  // `Uint8Array` over a `SharedArrayBuffer` is not assignable to it.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function toBase64Url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Whether this browser can do passkeys at all. */
export function passkeysSupported(): boolean {
  return (
    typeof globalThis.PublicKeyCredential === 'function' &&
    typeof navigator?.credentials?.create === 'function'
  );
}

export interface Challenge {
  readonly challenge: string;
  readonly rpId: string;
  readonly allowCredentials: readonly string[];
}

/** Creates a passkey and returns what the server needs to verify it. */
export async function createPasskey(
  challenge: Challenge,
  account: { id: string; name: string },
): Promise<{ credentialId: string; clientDataJSON: string; attestationObject: string } | null> {
  const credential = (await navigator.credentials.create({
    publicKey: {
      // The challenge is the server's, echoed back inside the signed client data.
      challenge: toBytes(challenge.challenge),
      rp: { id: challenge.rpId, name: 'Say Sooner' },
      user: {
        // The account id, not an email: a passkey should not carry an identifier we
        // would not otherwise hold, and an anonymous learner has no email anyway.
        // Same reason as `toBytes`: the DOM types want an `ArrayBuffer`-backed view.
        id: toBytes(btoa(account.id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')),
        name: account.name,
        displayName: account.name,
      },
      // ES256 first, RS256 second: the two the server verifies, in the order every
      // shipping platform authenticator prefers.
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        // A platform authenticator with a resident key is what makes this a *passkey*
        // rather than a second factor: it can be used to sign in on its own.
        residentKey: 'preferred',
        userVerification: 'preferred',
      },
      // The server does not consume an attestation statement, so asking for one would
      // collect device information we have no use for.
      attestation: 'none',
      timeout: 120_000,
    },
  })) as PublicKeyCredential | null;

  if (credential === null) return null;
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    credentialId: credential.id,
    clientDataJSON: toBase64Url(response.clientDataJSON),
    attestationObject: toBase64Url(response.attestationObject),
  };
}

/** Signs in with a passkey the learner already has. */
export async function usePasskey(challenge: Challenge): Promise<{
  credentialId: string;
  clientDataJSON: string;
  authenticatorData: string;
  signature: string;
} | null> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: toBytes(challenge.challenge),
      rpId: challenge.rpId,
      // Empty means "any passkey for this domain", which is the discoverable flow.
      allowCredentials: challenge.allowCredentials.map((id) => ({
        type: 'public-key' as const,
        id: toBytes(id),
      })),
      userVerification: 'preferred',
      timeout: 120_000,
    },
  })) as PublicKeyCredential | null;

  if (credential === null) return null;
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    credentialId: credential.id,
    clientDataJSON: toBase64Url(response.clientDataJSON),
    authenticatorData: toBase64Url(response.authenticatorData),
    signature: toBase64Url(response.signature),
  };
}
